// src/utils/macroTargets.js
// Per-meal macro targets and the rules that keep them coherent.
//
// Shared by QuizResultsPage (setting targets after the quiz) and AccountPanel
// (editing them later). These were duplicated in the quiz page only; once the
// account panel could edit the same numbers, two copies of the caps would have
// drifted the moment either changed.

/**
 * Per-meal ceilings from Claire's spec: no meal above 700 kcal / 61g protein,
 * snacks above 200 kcal / 15g protein. These are limits on what a customer may
 * set, not on what the portioning algorithm produces.
 */
export const MEAL_CAPS = {
  Breakfast: { calories: 700, proteinG: 61 },
  Lunch: { calories: 700, proteinG: 61 },
  Dinner: { calories: 700, proteinG: 61 },
  Snack: { calories: 200, proteinG: 15 },
  Snacks: { calories: 200, proteinG: 15 },
};

/** Human summary of the caps, shown under the editor. */
export const CAPS_SUMMARY =
  'Max 700 kcal and 61g protein per meal; snacks max 200 kcal and 15g protein.';

/**
 * Calories are not independent of the macros: kcal = 4·protein + 4·carbs + 9·fat.
 * Everything below preserves that identity, so the displayed calorie figure can
 * never disagree with the macros that produce it.
 */
export const caloriesFromMacros = ({ proteinG, carbsG, fatG }) =>
  Math.round((Number(proteinG) || 0) * 4 + (Number(carbsG) || 0) * 4 + (Number(fatG) || 0) * 9);

/**
 * Whole grams closest to `exact` whose 4p + 4c + 9f is exactly `calories`.
 * That needs fat ≡ calories (mod 4), so fat moves to the nearest such gram
 * count (at most 2g) and carbs take up the rest. Below ~24 kcal some figures
 * have no exact split; those fall back to plain rounding with derived calories.
 */
function fitMacros(exact, calories) {
  const cal = Math.round(calories);
  const r = ((cal % 4) + 4) % 4;
  if (cal < 9 * r) {
    const out = { proteinG: Math.round(exact.proteinG), carbsG: Math.round(exact.carbsG), fatG: Math.round(exact.fatG) };
    return { ...out, calories: caloriesFromMacros(out) };
  }
  const maxFat = r + 4 * Math.floor((cal / 9 - r) / 4);
  const below = Math.min(maxFat, Math.max(r, r + 4 * Math.floor((exact.fatG - r) / 4)));
  // Try the valid fat counts either side and keep whichever moves the grams least, in kcal.
  const best = [below, Math.min(maxFat, below + 4)].map((fatG) => {
    const proteinAndCarbs = (cal - 9 * fatG) / 4;
    const proteinG = Math.min(proteinAndCarbs, Math.max(0, Math.round(exact.proteinG)));
    const carbsG = proteinAndCarbs - proteinG;
    const cost = 4 * Math.abs(proteinG - exact.proteinG) + 4 * Math.abs(carbsG - exact.carbsG) + 9 * Math.abs(fatG - exact.fatG);
    return { proteinG, carbsG, fatG, calories: cal, cost };
  }).reduce((a, b) => (b.cost < a.cost ? b : a), { cost: Infinity });
  return { proteinG: best.proteinG, carbsG: best.carbsG, fatG: best.fatG, calories: cal };
}

/**
 * Rebalance a macro set after `changedKey` was edited.
 * Editing calories keeps the typed figure and scales protein/carbs/fat
 * proportionally to match it; editing protein, carbs or fat re-derives
 * calories. Fiber carries no calories, so it leaves them alone.
 * @param {object} draft {calories, proteinG, carbsG, fatG, fiberG}
 * @param {string} changedKey which field the user just edited
 */
export function reconcileMacros(draft, changedKey) {
  const next = { ...draft };
  const target = Math.round(Number(next.calories) || 0);
  if (changedKey === 'calories' && target > 0) {
    const current = (Number(next.proteinG) || 0) * 4 + (Number(next.carbsG) || 0) * 4 + (Number(next.fatG) || 0) * 9;
    const exact = current > 0
      ? {
        proteinG: (Number(next.proteinG) || 0) * (target / current),
        carbsG: (Number(next.carbsG) || 0) * (target / current),
        fatG: (Number(next.fatG) || 0) * (target / current),
      }
      // No existing macros to scale, so split the target evenly.
      : { proteinG: target / 12, carbsG: target / 12, fatG: target / 27 };
    return { ...next, ...fitMacros(exact, target) };
  }
  if (changedKey === 'fiberG') return next;
  next.calories = caloriesFromMacros(next);
  return next;
}

/**
 * Share of a target's calories coming from each macro, as percentages.
 * Returns null when there is nothing to divide, so callers can skip the bar
 * rather than render an empty one.
 */
export function macroSplit(t) {
  const kcal = {
    p: (Number(t.proteinG) || 0) * 4,
    c: (Number(t.carbsG) || 0) * 4,
    f: (Number(t.fatG) || 0) * 9,
  };
  const total = kcal.p + kcal.c + kcal.f;
  if (!total) return null;
  return { p: (kcal.p / total) * 100, c: (kcal.c / total) * 100, f: (kcal.f / total) * 100 };
}

/**
 * Which caps a given meal breaches, if any. Returns [] when the targets are
 * acceptable, so callers can both block saving and say what is wrong.
 * @returns {string[]} human-readable violations
 */
export function capViolations(meal, targets) {
  const caps = MEAL_CAPS[meal];
  if (!caps) return [];
  const out = [];
  const cal = Number(targets.calories) || 0;
  const pro = Number(targets.proteinG) || 0;
  if (caps.calories != null && cal > caps.calories) {
    out.push(`${meal} is capped at ${caps.calories} kcal`);
  }
  if (caps.proteinG != null && pro > caps.proteinG) {
    out.push(`${meal} is capped at ${caps.proteinG}g protein`);
  }
  return out;
}

/** True when every macro is a non-negative number and no cap is breached. */
export function isValidTarget(meal, targets) {
  const nums = ['calories', 'proteinG', 'carbsG', 'fatG', 'fiberG']
    .map((k) => Number(targets[k]));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return false;
  return capViolations(meal, targets).length === 0;
}

const MACRO_KEYS = ['proteinG', 'carbsG', 'fatG', 'fiberG'];

/**
 * Apply one committed edit to a set of targets. The field arrives as the raw
 * string the customer typed, so this runs once when they leave the field —
 * not on every keystroke. Reconciling per keystroke was SFLANDING-95: typing
 * "5" on the way to "500" rescaled every macro to a 5-kcal day, they rounded
 * to zero, and nothing could recover. An empty or zero calorie figure is
 * ignored for the same reason; an empty macro means 0.
 */
export function commitTargetEdit(targets, key, raw) {
  const n = Number(raw);
  if (key === 'calories') {
    if (!(n > 0)) return targets;
    return reconcileMacros({ ...targets, calories: n }, 'calories');
  }
  return reconcileMacros({ ...targets, [key]: Number.isFinite(n) && n > 0 ? Math.round(n) : 0 }, key);
}

// Older rows can carry calories that differ from their grams; the stored figure is what customers saw.
const mealCalories = (m) => (Number(m?.calories) > 0 ? Number(m.calories) : caloriesFromMacros(m || {}));

/**
 * Daily total from the per-meal targets: every field, calories included, is
 * the plain sum of the meals. The Snacks entry is one snack, so it counts
 * `snackCount` times — the same rule the backend uses when it sums the quiz's
 * meal breakdown.
 * @param {Object<string, object>} mealsByName {Breakfast: {...}, Lunch: {...}, Snacks: {...}}
 */
export function sumMealTargets(mealsByName, snackCount = 1) {
  const total = { calories: 0, proteinG: 0, carbsG: 0, fatG: 0, fiberG: 0 };
  Object.entries(mealsByName || {}).forEach(([meal, m]) => {
    const mult = meal === 'Snacks' ? (Number(snackCount) || 1) : 1;
    MACRO_KEYS.forEach((k) => { total[k] += (Number(m?.[k]) || 0) * mult; });
    total.calories += mealCalories(m) * mult;
  });
  Object.keys(total).forEach((k) => { total[k] = Math.round(total[k]); });
  return total;
}

/**
 * Split a whole-number daily `total` into whole numbers per meal, as close to
 * `exactByMeal` as possible while adding back up exactly. Snacks are rounded
 * first since each gram there counts `mult` times; the single meals then share
 * what is left, largest fractional part first.
 */
function apportion(total, exactByMeal, mult) {
  const names = Object.keys(exactByMeal);
  const singles = names.filter((n) => mult(n) === 1);
  const out = {};
  let left = total;
  names.forEach((n) => {
    out[n] = singles.includes(n) ? Math.floor(exactByMeal[n]) : Math.round(exactByMeal[n]);
    left -= out[n] * mult(n);
  });
  if (!singles.length) return out;
  const byFraction = [...singles].sort((a, b) => (exactByMeal[b] % 1) - (exactByMeal[a] % 1));
  // Snacks rounded up can overshoot: take it back from the single meals, smallest
  // fraction first, or from a snack itself when the single meals have none.
  for (let i = 0; left < 0; i += 1) {
    const n = byFraction[byFraction.length - 1 - (i % byFraction.length)];
    if (out[n] > 0) {
      out[n] -= 1; left += 1;
    } else if (!singles.some((s) => out[s] > 0)) {
      const snack = names.find((s) => mult(s) > 1 && out[s] > 0);
      out[snack] -= 1; left += mult(snack);
    }
  }
  for (let i = 0; left > 0; i += 1, left -= 1) out[byFraction[i % byFraction.length]] += 1;
  return out;
}

const MIN_MAIN_KCAL = 100;

/**
 * Spread an edited daily total back across the meals. Snacks keep their
 * targets (edit the snack card to change them); breakfast, lunch and dinner
 * absorb the change, unless the snacks alone exceed the new total.
 * by 'calories': the daily calories are split by each meal's share of the day,
 * so equal meals stay equal, and each meal's grams are fitted to its calories
 * in its own ratio. by 'macros': each macro is split by each meal's share of
 * it, and calories follow from the grams. Either way the meals add up exactly
 * to the day, field by field, and each meal is exactly 4p + 4c + 9f.
 * @param {'calories'|'macros'} by which daily figure the customer edited last
 */
export function distributeDailyTargets(mealsByName, newDaily, snackCount = 1, by = 'calories') {
  const names = Object.keys(mealsByName || {});
  if (!names.length) return {};
  const mult = (meal) => (meal === 'Snacks' ? (Number(snackCount) || 1) : 1);
  const value = (n, k) => (k === 'calories' ? mealCalories(mealsByName[n]) : Number(mealsByName[n]?.[k]) || 0);
  const day = Object.fromEntries(['calories', ...MACRO_KEYS].map((k) => [k, Math.round(Number(newDaily?.[k]) || 0)]));
  const mains = names.filter((n) => !/^Snacks?$/.test(n));

  // Split day[k] over the mains, holding snacks, unless that would leave the mains near nothing.
  const split = (k) => {
    const heldTotal = names.filter((n) => !mains.includes(n)).reduce((acc, n) => acc + value(n, k) * mult(n), 0);
    const floor = k === 'calories' ? MIN_MAIN_KCAL * mains.length : 0;
    const into = mains.length && day[k] - heldTotal >= floor ? mains : names;
    const total = into === names ? day[k] : day[k] - heldTotal;
    const oldTotal = into.reduce((acc, n) => acc + value(n, k) * mult(n), 0);
    const exact = Object.fromEntries(into.map((n) => [
      n, oldTotal > 0 ? (total * value(n, k)) / oldTotal : total / into.length / mult(n),
    ]));
    return { ...Object.fromEntries(names.map((n) => [n, value(n, k)])), ...apportion(total, exact, mult) };
  };

  const fiber = split('fiberG');
  const out = Object.fromEntries(names.map((n) => [n, { ...mealsByName[n], fiberG: fiber[n] }]));

  if (by === 'calories') {
    const cals = split('calories');
    names.forEach((n) => {
      if (cals[n] === mealCalories(mealsByName[n])) return;
      const m = mealsByName[n];
      const gramsKcal = (Number(m.proteinG) || 0) * 4 + (Number(m.carbsG) || 0) * 4 + (Number(m.fatG) || 0) * 9;
      const exact = gramsKcal > 0
        ? Object.fromEntries(['proteinG', 'carbsG', 'fatG'].map((k) => [k, ((Number(m[k]) || 0) * cals[n]) / gramsKcal]))
        : { proteinG: cals[n] / 12, carbsG: cals[n] / 12, fatG: cals[n] / 27 };
      Object.assign(out[n], fitMacros(exact, cals[n]));
    });
    return out;
  }

  ['proteinG', 'carbsG', 'fatG'].forEach((k) => {
    const grams = split(k);
    names.forEach((n) => { out[n][k] = grams[n]; });
  });
  names.forEach((n) => {
    if (['proteinG', 'carbsG', 'fatG'].some((k) => out[n][k] !== value(n, k))) out[n].calories = caloriesFromMacros(out[n]);
  });
  return out;
}
